import React, { useMemo } from "react";
import { ActivityIndicator, StyleSheet, View } from "react-native";
import { NavigationProp, RouteProp, useNavigation, useRoute } from "@react-navigation/native";

import CustomText from "../../components/common/general/CustomText/CustomText";
import { useGetModernizedCourseQuery, useGetModernizedCourseReviewAccessQuery } from "@axio/graphql/generated/graphql";
import type { RootStackParamList } from "../../navigation/navigation.types";
import CourseEvaluatorReviewContainer from "./courseModernizer/review/CourseEvaluatorReviewContainer";
import NoPermissionPanel from "./courseModernizer/review/components/NoPermissionPanel";
import { mapModernizedCourseToReviewData } from "./courseModernizer/review/reviewDataAdapter";

function CourseEvaluatorReviewView() {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, "Course Modernizer Review">>();

  const queryCourseId =
    typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("courseId")?.trim() : undefined;
  const courseId = queryCourseId || route.params?.courseId?.trim() || undefined;
  const { data, loading } = useGetModernizedCourseReviewAccessQuery({
    variables: { courseId: courseId ?? "" },
    skip: !courseId,
    fetchPolicy: "network-only",
  });
  const { data: courseData, loading: courseLoading } = useGetModernizedCourseQuery({
    variables: { courseId: courseId ?? "" },
    skip: !courseId || !data?.getModernizedCourseReviewAccess?.canEnterReview,
    fetchPolicy: "network-only",
  });

  const handleGoBack = () => navigation.navigate("Course Modernizer");

  const reviewData = useMemo(
    () => (courseData?.getModernizedCourse ? mapModernizedCourseToReviewData(courseData.getModernizedCourse) : null),
    [courseData]
  );
  const accessStatus = data?.getModernizedCourseReviewAccess;

  if (!courseId) {
    return (
      <NoPermissionPanel
        onGoBack={handleGoBack}
        title="Review Not Available"
        description="A modernized course id is required before you can open this review."
      />
    );
  }

  if (loading || (accessStatus?.canEnterReview && courseLoading)) {
    return (
      <View style={styles.loadingState}>
        <ActivityIndicator size="small" color="#3E68FE" />
        <CustomText
          text={loading ? "Checking review access..." : "Loading review..."}
          size="s"
          useTranslationText={false}
        />
      </View>
    );
  }

  if (!accessStatus?.canEnterReview) {
    const isModernizationRunning = accessStatus?.generationStatus === "RUNNING";

    return (
      <NoPermissionPanel
        onGoBack={handleGoBack}
        title={isModernizationRunning ? "Modernization In Progress" : "Access Denied"}
        description={
          isModernizationRunning
            ? "Modernization is currently in progress. Please refresh this page in a few minutes."
            : accessStatus?.message ||
              "You cannot enter this review yet. Wait for generation to complete and for the course modernization to be ready."
        }
      />
    );
  }

  if (!reviewData) {
    return (
      <NoPermissionPanel
        onGoBack={handleGoBack}
        title="Review Not Available"
        description="The modernized course review could not be loaded. Try again in a moment."
      />
    );
  }

  return (
    <CourseEvaluatorReviewContainer
      review={reviewData.review}
      activityDetails={reviewData.activityDetails}
      onExitReview={handleGoBack}
    />
  );
}

const styles = StyleSheet.create({
  loadingState: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    backgroundColor: "#F9FAFB",
  },
});

export default CourseEvaluatorReviewView;
